import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCluvioFilters, useTemplates } from '@/api/hooks';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { PageHeader, Skeleton, EmptyState, Field } from '@/components/primitives';
import { FilterPopover } from '@/components/FilterPopover';
import { Search, Filter, AlertCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function CluvioFiltersPage() {
  const { data: filters, isLoading, error, refetch } = useCluvioFilters();
  const { data: templates } = useTemplates();
  const [search, setSearch] = useState('');
  const [controlFilter, setControlFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Which templates use each Cluvio filter (via cluvioFilterID in fixed/variable filters)
  const usageByFilterId = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string }>>();
    for (const t of templates ?? []) {
      for (const f of [...t.fixedFilters, ...t.variableFilters]) {
        if (!f.cluvioFilterID) continue;
        const list = map.get(f.cluvioFilterID) ?? [];
        if (!list.some((x) => x.id === t.id)) list.push({ id: t.id, name: t.name });
        map.set(f.cluvioFilterID, list);
      }
    }
    return map;
  }, [templates]);

  const controlTypes = useMemo(
    () => [...new Set((filters ?? []).map((f) => f.controlType).filter(Boolean))].sort(),
    [filters]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (filters ?? [])
      .filter((f) => controlFilter === 'all' || f.controlType === controlFilter)
      .filter((f) => !q || f.name.toLowerCase().includes(q) || f.variable_name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filters, controlFilter, search]);

  // Possible duplicates: same display name used by more than one filter
  const duplicateGroups = useMemo(() => {
    const byName = new Map<string, NonNullable<typeof filters>>();
    for (const f of filters ?? []) {
      const key = f.name.trim().toLowerCase();
      if (!key) continue;
      byName.set(key, [...(byName.get(key) ?? []), f] as NonNullable<typeof filters>);
    }
    return [...byName.values()].filter((g) => g.length > 1);
  }, [filters]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Cluvio Filters"
        description="Every filter in the Cluvio account, with template usage — hunt duplicates and orphans."
        actions={
          <FilterPopover activeCount={controlFilter !== 'all' ? 1 : 0} onReset={() => setControlFilter('all')}>
            <Field label="Control type">
              <Select value={controlFilter} onChange={(e) => setControlFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                <option value="all">Any type</option>
                {controlTypes.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
          </FilterPopover>
        }
      />

      {duplicateGroups.length > 0 && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
            <div className="text-xs space-y-1.5 min-w-0">
              <p className="font-semibold text-amber-700 dark:text-amber-400">
                {duplicateGroups.length} name{duplicateGroups.length === 1 ? '' : 's'} shared by multiple filters
              </p>
              {duplicateGroups.map((g) => (
                <p key={g[0].name} className="text-muted-foreground truncate" title={g.map((f) => f.id).join(', ')}>
                  “{g[0].name}” × {g.length}
                  <span className="text-muted-foreground/70"> — {g.map((f) => f.variable_name).join(', ')}</span>
                </p>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or variable…" className="pl-9" />
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-border/60">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="px-4 py-3">
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertCircle}
            title="Failed to load filters"
            description={error.message}
            className="m-4"
            action={
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Filter} title="No filters match" description="Try a different search or control type." className="m-4" />
        ) : (
          <div className="divide-y divide-border/60">
            {filtered.map((f) => {
              const usedBy = usageByFilterId.get(f.id) ?? [];
              const isOpen = expandedId === f.id;
              return (
                <div key={f.id}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : f.id)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
                  >
                    <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                    <span className="text-xs font-medium truncate min-w-0" title={f.name}>
                      {f.name || <span className="text-muted-foreground">(unnamed)</span>}
                    </span>
                    <span className="hidden sm:block text-[11px] font-mono text-muted-foreground truncate min-w-0" title={f.variable_name}>
                      {f.variable_name}
                    </span>
                    <span className="ml-auto flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[9px]">
                        {f.controlType || 'unknown'}
                      </Badge>
                      <Badge variant={usedBy.length > 0 ? 'secondary' : 'outline'} className="text-[9px]">
                        {usedBy.length} template{usedBy.length === 1 ? '' : 's'}
                      </Badge>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-11 pb-3 pt-0.5 animate-fade-in">
                      {usedBy.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">Not used by any template</p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          Used by:{' '}
                          {usedBy.map((t, i) => (
                            <span key={t.id}>
                              {i > 0 && ', '}
                              <Link to={`/templates/${t.id}`} className="text-primary hover:underline">
                                {t.name}
                              </Link>
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
